/**
 * Schema.org Structured Data (JSON-LD) utilities for SEO
 *
 * This module provides functions to generate JSON-LD structured data
 * for better search engine visibility and rich snippets in Google.
 */

import { getImageUrl } from './api';
import { isLiveBlogValue } from './liveBlog';

// ============================================================================
// TYPES
// ============================================================================

export interface ArticleSchemaInput {
  headline: string;
  metaTitle?: string;
  description?: string;
  slug: string;
  heroImageKey?: string;
  heroImageCaption?: string;
  publishedAt?: string | number;
  updatedAt?: string | number;
  authors?: Array<{ name: string; slug?: string }>;
  sections?: Array<{ name: string; slug?: string }>;
  tags?: Array<{ name: string }>;
  wordCount?: number;
  articleBody?: string;
  customSlug?: string;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface WebPageSchemaInput {
  name: string;
  url: string;
  description?: string;
  inLanguage?: string | string[];
  breadcrumbs?: BreadcrumbItem[];
}

export interface OrganizationSchemaInput {
  name: string;
  url: string;
  logo: string;
  sameAs?: string[]; // Social media URLs
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convert a Date to IST (Indian Standard Time, UTC+5:30) ISO string
 * e.g., "2026-03-11T11:28:47.144" instead of "2026-03-11T05:58:47.144Z"
 */
function toISTISOString(date: Date): string {
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + istOffsetMs);
  return istDate.toISOString().replace('Z', '+05:30');
}

// ============================================================================
// CONSTANTS
// ============================================================================

const SITE_URL = 'https://www.dinamani.com';
const SITE_NAME = 'தினமணி';
const SITE_NAME_ENGLISH = 'Dinamani';
const LOGO_URL = 'https://media.dinamani.com/dinamani/2026-02-27/y9blh727/DinamaniLogo.png';
const SITE_NAVIGATION_ITEMS = [
  { name: 'Latest News', url: `${SITE_URL}/latest-news` },
  { name: 'Tamil Nadu', url: `${SITE_URL}/tamilnadu` },
  { name: 'India', url: `${SITE_URL}/india` },
  { name: 'World', url: `${SITE_URL}/world` },
  { name: 'Business', url: `${SITE_URL}/business` },
  { name: 'Sports', url: `${SITE_URL}/sports` },
  { name: 'Cinema', url: `${SITE_URL}/cinema` },
  { name: 'Health', url: `${SITE_URL}/health` },
  { name: 'Lifestyle', url: `${SITE_URL}/lifestyle` },
  { name: 'Editorial', url: `${SITE_URL}/editorial` },
  { name: 'Religion', url: `${SITE_URL}/religion` },
  { name: 'Astrology', url: `${SITE_URL}/astrology` },
  { name: 'Travel', url: `${SITE_URL}/travel` },
  { name: 'Education', url: `${SITE_URL}/education` },
  { name: 'Employment', url: `${SITE_URL}/employment` },
  { name: 'All Editions', url: `${SITE_URL}/all-editions` },
  { name: 'Elections', url: `${SITE_URL}/elections` },
  { name: 'Galleries', url: `${SITE_URL}/galleries` },
  { name: 'Videos', url: `${SITE_URL}/videos` },
  { name: 'Web Stories', url: `${SITE_URL}/webstories` },
];

// Publisher information (reused across articles)
const PUBLISHER_SCHEMA = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME_ENGLISH,
  alternateName: SITE_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: LOGO_URL,
    width: 600,
    height: 60,
  },
  sameAs: [
    'https://www.facebook.com/dinamaboruL',
    'https://twitter.com/dinamaboruL',
    'https://www.youtube.com/@Dinamaborultv',
    'https://www.instagram.com/dinamaboruL',
  ],
};

// ============================================================================
// SCHEMA GENERATORS
// ============================================================================

/**
 * Generate NewsArticle schema for article pages
 * This is the most important schema for news websites
 * Matches the exact format required by Google News
 */
export function generateNewsArticleSchema(input: ArticleSchemaInput): object {
  const {
    headline,
    description,
    slug,
    heroImageKey,
    heroImageCaption,
    publishedAt,
    updatedAt,
    authors,
    sections,
    tags,
    wordCount,
    articleBody,
    customSlug,
  } = input;

  const articleUrl = `${SITE_URL}/${slug}`;

  // Generate multiple image sizes for Google
  const images = heroImageKey ? [
    {
      '@type': 'ImageObject',
      url: `https://media.dinamani.com/${heroImageKey}?w=1200&h=675&auto=format%2Ccompress&fit=max&enlarge=true`,
      width: '1200',
      height: '675',
    },
    {
      '@type': 'ImageObject',
      url: `https://media.dinamani.com/${heroImageKey}?w=1200&h=900&auto=format%2Ccompress&fit=max&enlarge=true`,
      width: '1200',
      height: '900',
    },
    {
      '@type': 'ImageObject',
      url: `https://media.dinamani.com/${heroImageKey}?w=1200&h=1200&auto=format%2Ccompress&fit=max&enlarge=true`,
      width: '1200',
      height: '1200',
    },
  ] : [];

  // Build authors array with givenName
  const authorSchema = authors?.length
    ? authors.map((author) => ({
        '@type': 'Person',
        givenName: author.name,
        name: author.name,
        url: author.slug ? `${SITE_URL}/author/${author.slug}` : `${SITE_URL}/author/dinamani`,
      }))
    : [{ '@type': 'Person', givenName: SITE_NAME_ENGLISH, name: SITE_NAME_ENGLISH, url: `${SITE_URL}/author/dinamani` }];

  // Build article section
  const articleSection = sections?.[0]?.name || 'News';

  // Build keywords
  const keywords = tags?.map((tag) => tag.name).join(',') || '';

  // Format dates as IST ISO strings
  const datePublishedISO = publishedAt
    ? (typeof publishedAt === 'number' ? toISTISOString(new Date(publishedAt)) : toISTISOString(new Date(publishedAt)))
    : toISTISOString(new Date());
  const dateModifiedISO = updatedAt
    ? (typeof updatedAt === 'number' ? toISTISOString(new Date(updatedAt)) : toISTISOString(new Date(updatedAt)))
    : datePublishedISO;

  // Thumbnail URL
  const thumbnailUrl = heroImageKey
    ? `https://media.dinamani.com/${heroImageKey}?w=1200&h=675&auto=format%2Ccompress&fit=max&enlarge=true`
    : '';

  return {
    headline,
    image: images,
    url: articleUrl,
    datePublished: datePublishedISO,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    publisher: {
      '@type': 'Organization',
      '@context': 'http://schema.org',
      name: 'Dinamani',
      url: SITE_URL,
      logo: {
        '@context': 'http://schema.org',
        '@type': 'ImageObject',
        author: 'dinamani',
        contentUrl: 'https://media.dinamani.com/dinamani/2025-07-25/xsbo4ylj/DinamaniLogo.png',
        url: 'https://media.dinamani.com/dinamani/2025-07-25/xsbo4ylj/DinamaniLogo.png',
        name: 'logo',
        width: '',
        height: '',
      },
      sameAs: [
        'https://www.facebook.com/DinamaniDaily',
        'https://twitter.com/DinamaniDaily',
        'https://www.instagram.com/dinamanidaily/',
        'https://www.youtube.com/channel/UC3jcdpf8dWtljex9PyhSM6w?sub_confirmation=1',
        'https://www.whatsapp.com/channel/0029Va60JxGFcowBIEtwvB0G',
        'https://t.me/dinamaniofficial',
        'https://news.google.com/publications/CAAqJggKIiBDQklTRWdnTWFnNEtER1JwYm1GdFlXNXBMbU52YlNnQVAB?hl=ta&gl=IN&ceid=IN%3Ata',
      ],
      id: SITE_URL,
    },
    author: authorSchema,
    keywords,
    thumbnailUrl,
    articleBody: articleBody || '',
    dateCreated: datePublishedISO,
    dateModified: dateModifiedISO,
    name: headline,
    isPartOf: {
      '@type': 'WebPage',
      url: articleUrl,
      primaryImageOfPage: heroImageKey ? {
        '@type': 'ImageObject',
        url: thumbnailUrl,
        width: '1200',
        height: '675',
      } : undefined,
    },
    articleSection,
    alternativeHeadline: customSlug || '',
    description: description || '',
    '@type': 'NewsArticle',
    '@context': 'http://schema.org',
  };
}

/**
 * Generate Organization schema for the website
 * Should be included on homepage and about page
 */
export function generateOrganizationSchema(
  input?: Partial<OrganizationSchemaInput>
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsMediaOrganization',
    '@id': `${SITE_URL}/#organization`,
    name: input?.name || SITE_NAME_ENGLISH,
    alternateName: SITE_NAME,
    url: input?.url || SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: input?.logo || LOGO_URL,
      width: 600,
      height: 60,
    },
    sameAs: input?.sameAs || [
      'https://www.facebook.com/dinamaboruL',
      'https://twitter.com/dinamaboruL',
      'https://www.youtube.com/@Dinamaborultv',
      'https://www.instagram.com/dinamaboruL',
    ],
    // News-specific properties
    foundingDate: '1934-01-01',
    founder: {
      '@type': 'Person',
      name: 'T.V. Ramasubbaiyer',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Chennai',
      addressRegion: 'Tamil Nadu',
      addressCountry: 'IN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'dinamani@dinamani.com',
    },
  };
}

/**
 * Generate WebSite schema with search action
 * Enables Google search box feature in search results
 */
export function generateWebSiteSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME_ENGLISH,
    alternateName: SITE_NAME,
    url: SITE_URL,
    inLanguage: 'ta',
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search/{search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function generateWebPageSchema(input: WebPageSchemaInput): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${input.url}#webpage`,
    name: input.name,
    url: input.url,
    description: input.description || '',
    inLanguage: input.inLanguage || 'ta',
    isPartOf: {
      '@id': `${SITE_URL}/#website`,
    },
    breadcrumb: input.breadcrumbs?.length
      ? {
          '@type': 'BreadcrumbList',
          itemListElement: input.breadcrumbs.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url,
          })),
        }
      : undefined,
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
  };
}

export function generateSiteNavigationSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    '@id': `${SITE_URL}/#site-navigation`,
    name: SITE_NAVIGATION_ITEMS.map((item) => item.name),
    url: SITE_NAVIGATION_ITEMS.map((item) => item.url),
  };
}

/**
 * Generate BreadcrumbList schema for navigation
 */
export function generateBreadcrumbSchema(items: BreadcrumbItem[]): object {
  return {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate ImageObject schema for gallery/image pages
 */
export function generateImageSchema(input: {
  url: string;
  caption: string;
  width?: number;
  height?: number;
  author?: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    url: input.url,
    caption: input.caption,
    width: input.width || 1200,
    height: input.height || 630,
    author: input.author
      ? { '@type': 'Person', name: input.author }
      : PUBLISHER_SCHEMA,
    publisher: PUBLISHER_SCHEMA,
  };
}

/**
 * Generate VideoObject schema for video content
 */
export function generateVideoSchema(input: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string; // ISO 8601 format (e.g., "PT1M30S")
  embedUrl?: string;
  contentUrl?: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: input.name,
    description: input.description,
    thumbnailUrl: input.thumbnailUrl,
    uploadDate: input.uploadDate,
    duration: input.duration,
    embedUrl: input.embedUrl,
    contentUrl: input.contentUrl,
    publisher: PUBLISHER_SCHEMA,
  };
}

// ============================================================================
// EVENT SCHEMA (For Live Updates / Event Pages)
// ============================================================================

export interface EventSchemaInput {
  name: string;
  description?: string;
  startDate: string; // ISO 8601 format
  endDate?: string;
  location?: {
    name: string;
    address?: {
      streetAddress?: string;
      addressLocality?: string;
      addressRegion?: string;
      postalCode?: string;
      addressCountry?: string;
    };
  };
  imageUrl?: string;
  url?: string;
  performer?: string;
  organizer?: string;
  eventStatus?: 'EventScheduled' | 'EventCancelled' | 'EventPostponed' | 'EventRescheduled';
  eventAttendanceMode?: 'OfflineEventAttendanceMode' | 'OnlineEventAttendanceMode' | 'MixedEventAttendanceMode';
}

/**
 * Generate Event schema for event/live update pages
 * Reference: https://developers.google.com/search/docs/appearance/structured-data/event
 */
export function generateEventSchema(input: EventSchemaInput): object {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: input.name,
    description: input.description || '',
    startDate: input.startDate,
    eventStatus: `https://schema.org/${input.eventStatus || 'EventScheduled'}`,
    eventAttendanceMode: `https://schema.org/${input.eventAttendanceMode || 'MixedEventAttendanceMode'}`,
    organizer: {
      '@type': 'Organization',
      name: input.organizer || SITE_NAME_ENGLISH,
      url: SITE_URL,
    },
  };

  if (input.endDate) {
    schema.endDate = input.endDate;
  }

  if (input.location) {
    schema.location = {
      '@type': 'Place',
      name: input.location.name,
      address: input.location.address ? {
        '@type': 'PostalAddress',
        ...input.location.address,
      } : undefined,
    };
  }

  if (input.imageUrl) {
    schema.image = input.imageUrl;
  }

  if (input.url) {
    schema.url = input.url;
  }

  if (input.performer) {
    schema.performer = {
      '@type': 'Person',
      name: input.performer,
    };
  }

  return schema;
}

// ============================================================================
// LIVE BLOG POSTING SCHEMA (For Live Coverage Pages)
// ============================================================================

export interface LiveBlogPostingInput {
  headline: string;
  metaTitle?: string;
  description?: string;
  slug: string;
  coverageStartTime: string; // ISO 8601 format
  coverageEndTime?: string;
  publishedAt?: string;
  updatedAt?: string;
  heroImageKey?: string;
  authors?: Array<{ name: string; slug?: string }>;
  keywords?: string;
  liveBlogUpdates?: Array<{
    headline: string;
    articleBody: string;
    datePublished: string;
    url?: string;
  }>;
  aboutEvent?: {
    name: string;
    startDate: string;
    endDate?: string;
    description?: string;
    image?: string | string[];
    eventStatus?: string;
    offers?: {
      url?: string;
      price?: string | number;
      priceCurrency?: string;
      availability?: string;
      category?: string;
    };
    performer?: string | Array<{ name: string }>;
    organizer?: {
      name: string;
      url?: string;
    };
    location?: {
      name: string;
      addressLocality?: string;
      addressRegion?: string;
      addressCountry?: string;
    };
  };
}

/**
 * Generate LiveBlogPosting schema for live coverage/updates
 * Reference: https://developers.google.com/search/docs/appearance/structured-data/article
 */
export function generateLiveBlogPostingSchema(input: LiveBlogPostingInput): object {
  const articleUrl = `${SITE_URL}/${input.slug}`;
  const imageUrl = input.heroImageKey ? getImageUrl(input.heroImageKey) : null;

  // Build authors array
  const authorSchema = input.authors?.length
    ? input.authors.map((author) => ({
        '@type': 'Person',
        name: author.name,
        url: author.slug ? `${SITE_URL}/author/${author.slug}` : undefined,
      }))
    : [{ '@type': 'Organization', name: SITE_NAME_ENGLISH }];

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LiveBlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    url: articleUrl,
    headline: input.headline.slice(0, 110),
    description: input.description || '',
    datePublished: input.publishedAt || toISTISOString(new Date()),
    dateModified: input.updatedAt || input.publishedAt || toISTISOString(new Date()),
    coverageStartTime: input.coverageStartTime,
    author: authorSchema,
    publisher: PUBLISHER_SCHEMA,
    inLanguage: 'ta',
  };

  if (input.coverageEndTime) {
    schema.coverageEndTime = input.coverageEndTime;
  }

  if (input.keywords) {
    schema.keywords = input.keywords;
  }

  if (imageUrl) {
    schema.image = [
      `${imageUrl}?w=1200&ar=1:1&auto=format,compress`,
      `${imageUrl}?w=1200&ar=4:3&auto=format,compress`,
      `${imageUrl}?w=1200&ar=16:9&auto=format,compress`,
    ];
  }

  // Add event information if provided
  if (input.aboutEvent) {
    schema.about = {
      '@type': 'Event',
      startDate: input.aboutEvent.startDate,
      endDate: input.aboutEvent.endDate,
      name: input.aboutEvent.name,
      description: input.aboutEvent.description || input.description || '',
      image: input.aboutEvent.image || schema.image,
      eventStatus: input.aboutEvent.eventStatus
        ? `https://schema.org/${input.aboutEvent.eventStatus}`
        : undefined,
      offers: input.aboutEvent.offers
        ? {
            '@type': 'Offer',
            url: input.aboutEvent.offers.url || articleUrl,
            price: input.aboutEvent.offers.price,
            priceCurrency: input.aboutEvent.offers.priceCurrency,
            availability: input.aboutEvent.offers.availability
              ? `https://schema.org/${input.aboutEvent.offers.availability}`
              : undefined,
            category: input.aboutEvent.offers.category,
          }
        : undefined,
      performer: input.aboutEvent.performer
        ? (Array.isArray(input.aboutEvent.performer)
            ? input.aboutEvent.performer.map((performer) => ({
                '@type': 'Person',
                name: performer.name,
              }))
            : {
                '@type': 'Person',
                name: input.aboutEvent.performer,
              })
        : undefined,
      organizer: input.aboutEvent.organizer
        ? {
            '@type': 'Organization',
            name: input.aboutEvent.organizer.name,
            url: input.aboutEvent.organizer.url,
          }
        : undefined,
      location: input.aboutEvent.location ? {
        '@type': 'Place',
        name: input.aboutEvent.location.name,
        address: {
          '@type': 'PostalAddress',
          addressLocality: input.aboutEvent.location.addressLocality,
          addressRegion: input.aboutEvent.location.addressRegion,
          addressCountry: input.aboutEvent.location.addressCountry || 'IN',
        },
      } : undefined,
    };
  }

  // Add live blog updates if provided
  if (input.liveBlogUpdates && input.liveBlogUpdates.length > 0) {
    schema.liveBlogUpdate = input.liveBlogUpdates.map((update, index) => ({
      '@type': 'BlogPosting',
      headline: update.headline,
      url: update.url || `${articleUrl}#update${index + 1}`,
      datePublished: update.datePublished,
      articleBody: update.articleBody,
    }));
  }

  return schema;
}

// ============================================================================
// VIDEO PAGE SCHEMA (Combined NewsArticle + VideoObject)
// ============================================================================

export interface VideoPageSchemaInput {
  headline: string;
  metaTitle?: string;
  description?: string;
  slug: string;
  heroImageKey?: string;
  publishedAt?: string;
  updatedAt?: string;
  authors?: Array<{ name: string; slug?: string }>;
  sections?: Array<{ name: string; slug?: string }>;
  tags?: Array<{ name: string }>;
  videoUrl?: string;
  videoEmbedUrl?: string;
  videoDuration?: string; // ISO 8601 format (e.g., "PT1M30S")
}

/**
 * Generate combined schema for video pages (NewsArticle + VideoObject)
 */
export function generateVideoPageSchema(input: VideoPageSchemaInput): object[] {
  const articleUrl = `${SITE_URL}/${input.slug}`;
  const imageUrl = input.heroImageKey ? getImageUrl(input.heroImageKey) : null;
  const keywords = input.tags?.map((tag) => tag.name).join(', ') || '';

  // Build authors array
  const authorSchema = input.authors?.length
    ? input.authors.map((author) => ({
        '@type': 'Person',
        name: author.name,
        url: author.slug ? `${SITE_URL}/author/${author.slug}` : undefined,
      }))
    : [{ '@type': 'Organization', name: SITE_NAME_ENGLISH }];

  const articleSection = input.sections?.[0]?.name || 'Video';

  // NewsArticle schema
  const newsArticle: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    '@id': `${articleUrl}#article`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    headline: input.headline.slice(0, 110),
    description: input.description || '',
    url: articleUrl,
    datePublished: input.publishedAt || toISTISOString(new Date()),
    dateModified: input.updatedAt || input.publishedAt || toISTISOString(new Date()),
    author: authorSchema,
    publisher: PUBLISHER_SCHEMA,
    articleSection,
    keywords,
    inLanguage: 'ta',
    isAccessibleForFree: true,
  };

  if (imageUrl) {
    newsArticle.image = {
      '@type': 'ImageObject',
      url: imageUrl,
      width: 1200,
      height: 630,
    };
    newsArticle.thumbnailUrl = imageUrl;
  }

  // VideoObject schema
  const videoObject: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${articleUrl}#video`,
    name: input.headline,
    description: input.description || '',
    uploadDate: input.publishedAt || toISTISOString(new Date()),
    publisher: PUBLISHER_SCHEMA,
  };

  if (imageUrl) {
    videoObject.thumbnailUrl = [
      `${imageUrl}?w=1200&ar=1:1&auto=format,compress`,
      `${imageUrl}?w=1200&ar=4:3&auto=format,compress`,
      `${imageUrl}?w=1200&ar=16:9&auto=format,compress`,
    ];
  }

  if (input.videoUrl) {
    videoObject.contentUrl = input.videoUrl;
  }

  if (input.videoEmbedUrl) {
    videoObject.embedUrl = input.videoEmbedUrl;
  }

  if (input.videoDuration) {
    videoObject.duration = input.videoDuration;
  }

  return [newsArticle, videoObject];
}

/**
 * Generate combined schema for homepage
 */
export function generateHomePageSchema(): object[] {
  return [
    generateWebPageSchema({
      name: SITE_NAME_ENGLISH,
      url: SITE_URL,
      description: 'Latest Tamil news from Dinamani',
      inLanguage: 'ta',
      breadcrumbs: [{ name: 'Home', url: SITE_URL }],
    }),
    generateWebSiteSchema(),
    generateOrganizationSchema(),
  ];
}

/**
 * Generate combined schema for article pages
 */
export function generateArticlePageSchema(
  article: ArticleSchemaInput,
  breadcrumbs?: BreadcrumbItem[]
): object[] {
  const schemas: object[] = [
    generateWebPageSchema({
      name: article.metaTitle || article.headline,
      url: `${SITE_URL}/${article.slug}`,
      description: article.description,
      inLanguage: 'ta',
      breadcrumbs,
    }),
    generateNewsArticleSchema(article),
  ];

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(breadcrumbs));
  }

  return schemas;
}

/**
 * Generate combined schema for video article pages
 */
export function generateVideoArticlePageSchema(
  input: VideoPageSchemaInput,
  breadcrumbs?: BreadcrumbItem[]
): object[] {
  const schemas: object[] = [
    generateWebPageSchema({
      name: input.metaTitle || input.headline,
      url: `${SITE_URL}/${input.slug}`,
      description: input.description,
      inLanguage: 'ta',
      breadcrumbs,
    }),
    ...generateVideoPageSchema(input),
  ];

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(breadcrumbs));
  }

  return schemas;
}

/**
 * Generate combined schema for live blog pages
 */
export function generateLiveBlogPageSchema(
  input: LiveBlogPostingInput,
  breadcrumbs?: BreadcrumbItem[]
): object[] {
  const schemas: object[] = [
    generateWebPageSchema({
      name: input.metaTitle || input.headline,
      url: `${SITE_URL}/${input.slug}`,
      description: input.description,
      inLanguage: 'ta',
      breadcrumbs,
    }),
    generateLiveBlogPostingSchema(input),
  ];

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(breadcrumbs));
  }

  return schemas;
}

// ============================================================================
// REACT COMPONENT HELPERS
// ============================================================================

/**
 * Convert schema object to JSON-LD script tag content
 */
export function schemaToJsonLd(schema: object | object[]): string {
  return JSON.stringify(schema);
}

/**
 * Generate the complete script tag HTML for schema
 */
export function generateSchemaScriptTag(schema: object | object[]): string {
  return `<script type="application/ld+json">${JSON.stringify(schema)}</script>`;
}

// ============================================================================
// RAW API RESPONSE SCHEMA GENERATORS (for client-side/infinite scroll)
// ============================================================================

const MEDIA_BASE_URL = 'https://media.dinamani.com';
const PUBLISHER_LOGO_URL = 'https://media.dinamani.com/dinamani/2025-07-25/xsbo4ylj/DinamaniLogo.png';

/**
 * Generate image URL with transformation parameters
 */
function generateSchemaImageUrl(heroImageS3Key: string, width: number, height: number): string {
  if (!heroImageS3Key) return '';
  return `${MEDIA_BASE_URL}/${heroImageS3Key}?w=${width}&h=${height}&auto=format%2Ccompress&fit=max&enlarge=true`;
}

/**
 * Extract plain text from story cards for articleBody
 */
function extractArticleBodyFromCards(cards: any[]): string {
  if (!cards || cards.length === 0) return '';

  const textParts: string[] = [];

  for (const card of cards) {
    const storyElements = card['story-elements'] || [];
    for (const element of storyElements) {
      if (element.text) {
        // Strip HTML tags and decode entities
        const plainText = element.text
          .replace(/<[^>]*>/g, '')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&#61;/g, '=')
          .replace(/\s+/g, ' ')
          .trim();
        if (plainText) {
          textParts.push(plainText);
        }
      }
    }
  }

  return textParts.join('.');
}

/**
 * Generate BreadcrumbList schema from raw API story data
 */
export function generateBreadcrumbSchemaFromStory(story: any): object {
  const itemListElement: any[] = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_URL,
    },
  ];

  let position = 2;

  // Add section breadcrumb
  if (story.sections?.[0]) {
    itemListElement.push({
      '@type': 'ListItem',
      position: position++,
      name: story.sections[0].name || story.sections[0]['display-name'],
      item: `${SITE_URL}/${story.sections[0].slug}`,
    });
  }

  // Add article breadcrumb
  itemListElement.push({
    '@type': 'ListItem',
    position: position,
    name: story.headline,
    item: `${SITE_URL}/${story.slug}`,
  });

  return {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}

/**
 * Generate NewsArticle schema from raw API story data
 * Matches the exact format provided by the user
 */
export function generateNewsArticleSchemaFromStory(story: any): object {
  const heroImageS3Key = story['hero-image-s3-key'] || '';
  const articleUrl = `${SITE_URL}/${story.slug}`;

  // Generate multiple image sizes for Google
  const images = heroImageS3Key ? [
    {
      '@type': 'ImageObject',
      url: generateSchemaImageUrl(heroImageS3Key, 1200, 675),
      width: '1200',
      height: '675',
    },
    {
      '@type': 'ImageObject',
      url: generateSchemaImageUrl(heroImageS3Key, 1200, 900),
      width: '1200',
      height: '900',
    },
    {
      '@type': 'ImageObject',
      url: generateSchemaImageUrl(heroImageS3Key, 1200, 1200),
      width: '1200',
      height: '1200',
    },
  ] : [];

  // Build authors array
  const authors = (story.authors || []).map((author: any) => ({
    '@type': 'Person',
    givenName: author.name,
    name: author.name,
    url: `${SITE_URL}/author/${author.slug}`,
  }));

  // If no authors, use default
  if (authors.length === 0 && story['author-name']) {
    authors.push({
      '@type': 'Person',
      givenName: story['author-name'],
      name: story['author-name'],
      url: `${SITE_URL}/author/${story.authors?.[0]?.slug || 'dinamani'}`,
    });
  }

  // Build keywords from tags
  const keywords = (story.tags || []).map((tag: any) => tag.name).join(',');

  // Extract article body from cards
  const articleBody = extractArticleBodyFromCards(story.cards);

  // Format dates as IST
  const datePublished = story['published-at'] || story['first-published-at']
    ? toISTISOString(new Date(story['published-at'] || story['first-published-at']))
    : toISTISOString(new Date());

  const dateModified = story['updated-at'] || story['content-updated-at']
    ? toISTISOString(new Date(story['updated-at'] || story['content-updated-at']))
    : datePublished;

  // Get article section
  const articleSection = story.sections?.[0]?.name || story.sections?.[0]?.['display-name'] || 'News';

  // Get description
  const description = story.seo?.['meta-description'] || story.summary || story.subheadline || '';

  return {
    headline: story.headline,
    image: images,
    url: articleUrl,
    datePublished,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    publisher: {
      '@type': 'Organization',
      '@context': 'http://schema.org',
      name: 'Dinamani',
      url: SITE_URL,
      logo: {
        '@context': 'http://schema.org',
        '@type': 'ImageObject',
        author: 'dinamani',
        contentUrl: PUBLISHER_LOGO_URL,
        url: PUBLISHER_LOGO_URL,
        name: 'logo',
        width: '',
        height: '',
      },
      sameAs: [
        'https://www.facebook.com/DinamaniDaily',
        'https://twitter.com/DinamaniDaily',
        'https://www.instagram.com/dinamanidaily/',
        'https://www.youtube.com/channel/UC3jcdpf8dWtljex9PyhSM6w?sub_confirmation=1',
        'https://www.whatsapp.com/channel/0029Va60JxGFcowBIEtwvB0G',
        't.me/dinamaniofficial',
        'https://news.google.com/publications/CAAqJggKIiBDQklTRWdnTWFnNEtER1JwYm1GdFlXNXBMbU52YlNnQVAB?hl=ta&gl=IN&ceid=IN%3Ata',
      ],
      id: SITE_URL,
    },
    author: authors,
    keywords,
    thumbnailUrl: heroImageS3Key ? generateSchemaImageUrl(heroImageS3Key, 1200, 675) : '',
    articleBody,
    dateCreated: datePublished,
    dateModified,
    name: story.headline,
    isPartOf: {
      '@type': 'WebPage',
      url: articleUrl,
      primaryImageOfPage: heroImageS3Key ? {
        '@type': 'ImageObject',
        url: generateSchemaImageUrl(heroImageS3Key, 1200, 675),
        width: '1200',
        height: '675',
      } : undefined,
    },
    articleSection,
    alternativeHeadline: story['custom-slug'] || '',
    description,
    '@type': 'NewsArticle',
    '@context': 'http://schema.org',
  };
}

/**
 * Generate combined schema (BreadcrumbList + NewsArticle) from raw API story data
 * This is used for client-side schema updates during infinite scroll
 */
export function generateArticleSchemaFromStory(story: any): object[] {
  const breadcrumbSchema = generateBreadcrumbSchemaFromStory(story) as {
    itemListElement: Array<{ name: string; item: string }>;
  };
  const breadcrumbItems: BreadcrumbItem[] = breadcrumbSchema.itemListElement.map((item) => ({
    name: item.name,
    url: item.item,
  }));

  return [
    generateWebPageSchema({
      name: story.seo?.['meta-title'] || story.headline,
      url: `${SITE_URL}/${story.slug}`,
      description: story.seo?.['meta-description'] || story.summary || story.subheadline || '',
      inLanguage: 'ta',
      breadcrumbs: breadcrumbItems,
    }),
    breadcrumbSchema,
    generateNewsArticleSchemaFromStory(story),
  ];
}

// ============================================================================
// VIDEO PAGE SCHEMA (VideoObject)
// ============================================================================

/**
 * Extract video embed URL from story cards
 */
function extractVideoEmbedUrl(cards: any[]): string | undefined {
  if (!cards) return undefined;

  for (const card of cards) {
    const storyElements = card['story-elements'] || [];
    for (const element of storyElements) {
      // Check for YouTube embeds
      if (element.type === 'youtube-video' && element.url) {
        return element.url;
      }
      // Check for jsembed with video
      if (element.type === 'jsembed' && element.metadata?.['video-url']) {
        return element.metadata['video-url'];
      }
      // Check for direct video embeds
      if (element.type === 'video' && element.url) {
        return element.url;
      }
      // Check for embed URL in subtype
      if (element.subtype === 'video' && element['embed-url']) {
        return element['embed-url'];
      }
    }
  }
  return undefined;
}

/**
 * Generate VideoObject schema from raw API story data
 * Matches the exact format for video pages
 */
export function generateVideoObjectSchemaFromStory(story: any): object {
  const heroImageS3Key = story['hero-image-s3-key'] || '';
  const articleUrl = `${SITE_URL}/${story.slug}`;

  // Get thumbnail URL
  const thumbnailUrl = heroImageS3Key
    ? generateSchemaImageUrl(heroImageS3Key, 1200, 675)
    : '';

  // Build authors array
  const authors = (story.authors || []).map((author: any) => ({
    '@type': 'Person',
    givenName: author.name,
    name: author.name,
    url: `${SITE_URL}/author/${author.slug || 'dinamani'}`,
  }));

  // If no authors, use default
  if (authors.length === 0) {
    authors.push({
      '@type': 'Person',
      givenName: story['author-name'] || 'இணையதளச் செய்திப் பிரிவு',
      name: story['author-name'] || 'இணையதளச் செய்திப் பிரிவு',
      url: `${SITE_URL}/author/${story.authors?.[0]?.slug || 'innaiytllceytippirivu'}`,
    });
  }

  // Build keywords from tags
  const keywords = (story.tags || []).map((tag: any) => tag.name).join(',');

  // Format dates as IST
  const datePublished = story['published-at'] || story['first-published-at']
    ? toISTISOString(new Date(story['published-at'] || story['first-published-at']))
    : toISTISOString(new Date());

  const dateModified = story['updated-at'] || story['content-updated-at']
    ? toISTISOString(new Date(story['updated-at'] || story['content-updated-at']))
    : datePublished;

  // Get description
  const description = story.seo?.['meta-description'] || story.summary || story.subheadline || story.headline;

  // Extract video embed URL from cards
  const embedUrl = extractVideoEmbedUrl(story.cards);

  return {
    headline: story.headline,
    image: thumbnailUrl ? [thumbnailUrl] : [],
    url: articleUrl,
    datePublished,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    publisher: {
      '@type': 'Organization',
      '@context': 'http://schema.org',
      name: 'Dinamani',
      url: SITE_URL,
      logo: {
        '@context': 'http://schema.org',
        '@type': 'ImageObject',
        author: 'dinamani',
        contentUrl: PUBLISHER_LOGO_URL,
        url: PUBLISHER_LOGO_URL,
        name: 'logo',
        width: '',
        height: '',
      },
      sameAs: [
        'https://www.facebook.com/DinamaniDaily',
        'https://twitter.com/DinamaniDaily',
        'https://www.instagram.com/dinamanidaily/',
        'https://www.youtube.com/channel/UC3jcdpf8dWtljex9PyhSM6w?sub_confirmation=1',
        'https://www.whatsapp.com/channel/0029Va60JxGFcowBIEtwvB0G',
        'https://t.me/dinamaniofficial',
        'https://news.google.com/publications/CAAqJggKIiBDQklTRWdnTWFnNEtER1JwYm1GdFlXNXBMbU52YlNnQVAB?hl=ta&gl=IN&ceid=IN%3Ata',
      ],
      id: SITE_URL,
    },
    author: authors,
    keywords,
    dateCreated: datePublished,
    dateModified,
    description,
    name: story.headline,
    thumbnailUrl: thumbnailUrl ? [thumbnailUrl] : [],
    uploadDate: datePublished,
    embedUrl: embedUrl || '',
    '@type': 'VideoObject',
    '@context': 'http://schema.org',
  };
}

/**
 * Generate combined schema for video pages from raw API story data
 * Returns VideoObject + BreadcrumbList
 */
export function generateVideoPageSchemaFromStory(story: any): object[] {
  const breadcrumbSchema = generateBreadcrumbSchemaFromStory(story) as {
    itemListElement: Array<{ name: string; item: string }>;
  };
  const breadcrumbItems: BreadcrumbItem[] = breadcrumbSchema.itemListElement.map((item) => ({
    name: item.name,
    url: item.item,
  }));

  return [
    generateWebPageSchema({
      name: story.seo?.['meta-title'] || story.headline,
      url: `${SITE_URL}/${story.slug}`,
      description: story.seo?.['meta-description'] || story.summary || story.subheadline || '',
      inLanguage: 'ta',
      breadcrumbs: breadcrumbItems,
    }),
    generateVideoObjectSchemaFromStory(story),
    breadcrumbSchema,
  ];
}

// ============================================================================
// VISUAL STORY SCHEMA (NewsArticle)
// ============================================================================

/**
 * Generate NewsArticle schema for visual stories from raw API story data
 * Matches the exact format for visual story pages
 */
export function generateVisualStorySchemaFromStory(story: any): object {
  const heroImageS3Key = story['hero-image-s3-key'] || '';
  const articleUrl = `${SITE_URL}/${story.slug}`;

  // Generate multiple image sizes for Google (visual stories need multiple aspect ratios)
  const images = heroImageS3Key ? [
    {
      '@type': 'ImageObject',
      url: generateSchemaImageUrl(heroImageS3Key, 1200, 675),
      width: '1200',
      height: '675',
    },
    {
      '@type': 'ImageObject',
      url: generateSchemaImageUrl(heroImageS3Key, 1200, 900),
      width: '1200',
      height: '900',
    },
    {
      '@type': 'ImageObject',
      url: generateSchemaImageUrl(heroImageS3Key, 1200, 1200),
      width: '1200',
      height: '1200',
    },
  ] : [];

  // Get thumbnail URL
  const thumbnailUrl = heroImageS3Key
    ? generateSchemaImageUrl(heroImageS3Key, 1200, 675)
    : '';

  // Build authors array
  const authors = (story.authors || []).map((author: any) => ({
    '@type': 'Person',
    givenName: author.name,
    name: author.name,
    url: `${SITE_URL}/author/${author.slug || 'dinamani'}`,
  }));

  // If no authors, use default
  if (authors.length === 0) {
    authors.push({
      '@type': 'Person',
      givenName: story['author-name'] || 'இணையதளச் செய்திப் பிரிவு',
      name: story['author-name'] || 'இணையதளச் செய்திப் பிரிவு',
      url: `${SITE_URL}/author/${story.authors?.[0]?.slug || 'innaiytllceytippirivu'}`,
    });
  }

  // Build keywords from tags
  const keywords = (story.tags || []).map((tag: any) => tag.name).join(',');

  // Extract article body from cards
  const articleBody = extractArticleBodyFromCards(story.cards);

  // Format dates as IST
  const datePublished = story['published-at'] || story['first-published-at']
    ? toISTISOString(new Date(story['published-at'] || story['first-published-at']))
    : toISTISOString(new Date());

  const dateModified = story['updated-at'] || story['content-updated-at']
    ? toISTISOString(new Date(story['updated-at'] || story['content-updated-at']))
    : datePublished;

  // Get article section - for visual stories, default to "விஷுவல் ஸ்டோரிஸ்"
  const articleSection = story.sections?.[0]?.name || story.sections?.[0]?.['display-name'] || 'விஷுவல் ஸ்டோரிஸ்';

  // Get description
  const description = story.seo?.['meta-description'] || story.summary || story.subheadline || null;

  return {
    headline: story.headline,
    image: images,
    url: articleUrl,
    datePublished,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    publisher: {
      '@type': 'Organization',
      '@context': 'http://schema.org',
      name: 'Dinamani',
      url: SITE_URL,
      logo: {
        '@context': 'http://schema.org',
        '@type': 'ImageObject',
        author: 'dinamani',
        contentUrl: PUBLISHER_LOGO_URL,
        url: PUBLISHER_LOGO_URL,
        name: 'logo',
        width: '',
        height: '',
      },
      sameAs: [
        'https://www.facebook.com/DinamaniDaily',
        'https://twitter.com/DinamaniDaily',
        'https://www.instagram.com/dinamanidaily/',
        'https://www.youtube.com/channel/UC3jcdpf8dWtljex9PyhSM6w?sub_confirmation=1',
        'https://www.whatsapp.com/channel/0029Va60JxGFcowBIEtwvB0G',
        'https://t.me/dinamaniofficial',
        'https://news.google.com/publications/CAAqJggKIiBDQklTRWdnTWFnNEtER1JwYm1GdFlXNXBMbU52YlNnQVAB?hl=ta&gl=IN&ceid=IN%3Ata',
      ],
      id: SITE_URL,
    },
    author: authors,
    keywords,
    thumbnailUrl,
    articleBody,
    dateCreated: datePublished,
    dateModified,
    name: story.headline,
    isPartOf: {
      '@type': 'WebPage',
      url: articleUrl,
      primaryImageOfPage: thumbnailUrl ? {
        '@type': 'ImageObject',
        url: thumbnailUrl,
        width: '1200',
        height: '675',
      } : undefined,
    },
    articleSection,
    alternativeHeadline: story['custom-slug'] || '',
    description,
    '@type': 'NewsArticle',
    '@context': 'http://schema.org',
  };
}

/**
 * Generate combined schema for visual story pages from raw API story data
 * Returns NewsArticle + BreadcrumbList
 */
export function generateVisualStoryPageSchemaFromStory(story: any): object[] {
  const breadcrumbSchema = generateBreadcrumbSchemaFromStory(story) as {
    itemListElement: Array<{ name: string; item: string }>;
  };
  const breadcrumbItems: BreadcrumbItem[] = breadcrumbSchema.itemListElement.map((item) => ({
    name: item.name,
    url: item.item,
  }));

  return [
    generateWebPageSchema({
      name: story.seo?.['meta-title'] || story.headline,
      url: `${SITE_URL}/${story.slug}`,
      description: story.seo?.['meta-description'] || story.summary || story.subheadline || '',
      inLanguage: 'ta',
      breadcrumbs: breadcrumbItems,
    }),
    generateVisualStorySchemaFromStory(story),
    breadcrumbSchema,
  ];
}

// ============================================================================
// LIVE BLOG POSTING SCHEMA (From Raw API Response)
// ============================================================================

/**
 * Extract headline/title from a live blog card
 */
function extractCardHeadline(card: any): string {
  const storyElements = card['story-elements'] || [];

  // Look for title element first
  for (const element of storyElements) {
    if (element.type === 'title' && element.text) {
      return element.text.replace(/<[^>]*>/g, '').trim();
    }
  }

  // Fallback to first text element
  for (const element of storyElements) {
    if (element.text) {
      const text = element.text.replace(/<[^>]*>/g, '').trim();
      if (text) return text.substring(0, 100);
    }
  }

  return 'Update';
}

/**
 * Extract article body from a live blog card
 */
function extractCardArticleBody(card: any): string {
  const storyElements = card['story-elements'] || [];
  const textParts: string[] = [];

  for (const element of storyElements) {
    if (element.text && element.type !== 'title') {
      const plainText = element.text
        .replace(/<[^>]*>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim();
      if (plainText) {
        textParts.push(plainText);
      }
    }
  }

  return textParts.join(' ') || extractCardHeadline(card);
}

/**
 * Generate LiveBlogPosting schema from raw API story data
 * For live blog articles with is-live-blog: true or story-template: "live-blog"
 */
export function generateLiveBlogPostingSchemaFromStory(story: any): object {
  const heroImageS3Key = story['hero-image-s3-key'] || '';
  const articleUrl = `${SITE_URL}/${story.slug}`;
  const storyMetadata = story.metadata || story.meta || story.seo || {};
  const isLiveBlogStory = isLiveBlogValue(story['story-template']) || isLiveBlogValue(story['is-live-blog']);

  // Generate multiple image sizes for Google
  const images = heroImageS3Key ? [
    `${MEDIA_BASE_URL}/${heroImageS3Key}?w=1200&h=1200&auto=format,compress`,
    `${MEDIA_BASE_URL}/${heroImageS3Key}?w=1200&h=900&auto=format,compress`,
    `${MEDIA_BASE_URL}/${heroImageS3Key}?w=1200&h=675&auto=format,compress`,
  ] : [];

  // Build authors array
  const authors = (story.authors || []).map((author: any) => ({
    '@type': 'Person',
    name: author.name,
    url: author.slug ? `${SITE_URL}/author/${author.slug}` : undefined,
  }));

  // If no authors, use default
  if (authors.length === 0 && story['author-name']) {
    authors.push({
      '@type': 'Person',
      name: story['author-name'],
    });
  }

  if (authors.length === 0) {
    authors.push({
      '@type': 'Organization',
      name: 'Dinamani',
    });
  }

  // Build keywords from tags
  const keywords = (story.tags || []).map((tag: any) => tag.name).join(', ') || 'Live Coverage, LiveBlogPosting';

  // Format dates as IST
  const firstPublished = story['first-published-at'] || story['published-at'];
  const lastPublished = story['last-published-at'] || story['content-updated-at'];

  const datePublished = firstPublished
    ? toISTISOString(new Date(firstPublished))
    : toISTISOString(new Date());

  const dateModified = lastPublished
    ? toISTISOString(new Date(lastPublished))
    : datePublished;

  // Coverage times
  const coverageStartTime = datePublished;
  const coverageEndTime = isLiveBlogStory ? undefined : dateModified;

  // Get description
  const description = story.seo?.['meta-description'] || story.summary || story.subheadline || '';
  const eventStatus = isLiveBlogStory ? 'EventInProgress' : 'EventCompleted';
  const organizerName =
    story.organizer?.name ||
    storyMetadata.organizer_name ||
    storyMetadata.organizer ||
    'Dinamani';
  const organizerUrl =
    story.organizer?.url ||
    storyMetadata.organizer_url ||
    SITE_URL;
  const performerNames = [
    ...(Array.isArray(story.performers) ? story.performers.map((performer: any) => performer?.name || performer) : []),
    ...(Array.isArray(storyMetadata.performers) ? storyMetadata.performers.map((performer: any) => performer?.name || performer) : []),
    story.performer?.name || story.performer,
    storyMetadata.performer_name || storyMetadata.performer,
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  const eventOffers = story.offers || storyMetadata.offers;

  // Build liveBlogUpdate array from cards
  const cards = story.cards || [];
  const liveBlogUpdate = cards.map((card: any, index: number) => {
    const cardTime = card['card-added-at'] || card['card-event-at'] || card['card-updated-at'];
    const cardDatePublished = cardTime
      ? toISTISOString(new Date(cardTime))
      : datePublished;

    return {
      '@type': 'BlogPosting',
      headline: extractCardHeadline(card),
      url: `${articleUrl}#${card.id || `update${index + 1}`}`,
      datePublished: cardDatePublished,
      articleBody: extractCardArticleBody(card),
    };
  });

  // Build the schema
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LiveBlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    url: articleUrl,
    datePublished,
    dateModified,
    coverageStartTime,
    headline: story.headline,
    description,
    image: images,
    keywords,
    publisher: {
      '@type': 'Organization',
      name: 'Dinamani',
      logo: {
        '@type': 'ImageObject',
        url: PUBLISHER_LOGO_URL,
        height: '60',
        width: '600',
      },
    },
    author: authors.length === 1 ? authors[0] : authors,
    inLanguage: 'ta',
  };

  // Add coverage end time if blog is ended
  if (coverageEndTime) {
    schema.coverageEndTime = coverageEndTime;
  }

  // Add about event if section info available
  if (story.sections?.[0]) {
    schema.about = {
      '@type': 'Event',
      startDate: coverageStartTime,
      endDate: coverageEndTime || dateModified,
      name: story.headline,
      description,
      image: images,
      eventStatus: `https://schema.org/${eventStatus}`,
      offers: eventOffers
        ? {
            '@type': 'Offer',
            url: eventOffers.url || articleUrl,
            price: eventOffers.price,
            priceCurrency: eventOffers.priceCurrency,
            availability: eventOffers.availability
              ? `https://schema.org/${eventOffers.availability}`
              : undefined,
            category: eventOffers.category,
          }
        : undefined,
      performer: performerNames.length > 0
        ? performerNames.map((name) => ({
            '@type': 'Person',
            name,
          }))
        : undefined,
      organizer: {
        '@type': 'Organization',
        name: organizerName,
        url: organizerUrl,
      },
      location: {
        '@type': 'Place',
        name: story.sections[0].name || story.sections[0]['display-name'],
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Chennai',
          addressRegion: 'Tamil Nadu',
          addressCountry: 'IN',
        },
      },
    };
  }

  // Add live blog updates
  if (liveBlogUpdate.length > 0) {
    schema.liveBlogUpdate = liveBlogUpdate;
  }

  return schema;
}

/**
 * Generate combined schema for live blog pages from raw API story data
 * Returns LiveBlogPosting + BreadcrumbList
 */
export function generateLiveBlogPageSchemaFromStory(story: any): object[] {
  const breadcrumbSchema = generateBreadcrumbSchemaFromStory(story) as {
    itemListElement: Array<{ name: string; item: string }>;
  };
  const breadcrumbItems: BreadcrumbItem[] = breadcrumbSchema.itemListElement.map((item) => ({
    name: item.name,
    url: item.item,
  }));

  return [
    generateWebPageSchema({
      name: story.seo?.['meta-title'] || story.headline,
      url: `${SITE_URL}/${story.slug}`,
      description: story.seo?.['meta-description'] || story.summary || story.subheadline || '',
      inLanguage: 'ta',
      breadcrumbs: breadcrumbItems,
    }),
    generateLiveBlogPostingSchemaFromStory(story),
    breadcrumbSchema,
  ];
}
